import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { witnessStatementService } from '../../services/api/witnessStatementService';
import StatementTemplateSelector from './StatementTemplateSelector';
import WitnessStatementForm from './WitnessStatementForm';

const WitnessStatementList = () => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingStatement, setEditingStatement] = useState(null);

  useEffect(() => {
    loadStatements();
  }, [filterStatus]);

  const loadStatements = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await witnessStatementService.listStatements({ status: filterStatus });
      setStatements(response.statements || []);
    } catch (err) {
      console.error('Error loading statements:', err);
      setError(err.message || 'Failed to load statements');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'warning';
      case 'submitted':
        return 'info';
      case 'finalized':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleTemplateSelect = (template, eventData = null) => {
    setSelectedTemplate(template);
    setShowTemplateSelector(false);
    // Store event data if creating from event
    if (eventData) {
      setEditingStatement({ eventData, template });
    }
  };

  const handleStatementSaved = () => {
    setSelectedTemplate(null);
    setEditingStatement(null);
    loadStatements();
  };

  const handleEditStatement = (statement) => {
    console.log('[WitnessStatementList] Editing statement:', statement);
    console.log('[WitnessStatementList] Has draft_data:', !!statement.draft_data);
    console.log('[WitnessStatementList] draft_data keys:', statement.draft_data ? Object.keys(statement.draft_data) : 'none');
    setEditingStatement(statement);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">My Witness Statements</Typography>
        {/* Create Statement button removed - statements must be created from events */}
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <Chip
          label="All"
          onClick={() => setFilterStatus(null)}
          color={filterStatus === null ? 'primary' : 'default'}
        />
        <Chip
          label="Draft"
          onClick={() => setFilterStatus('draft')}
          color={filterStatus === 'draft' ? 'primary' : 'default'}
        />
        <Chip
          label="Submitted"
          onClick={() => setFilterStatus('submitted')}
          color={filterStatus === 'submitted' ? 'primary' : 'default'}
        />
        <Chip
          label="Finalized"
          onClick={() => setFilterStatus('finalized')}
          color={filterStatus === 'finalized' ? 'primary' : 'default'}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Statement ID</TableCell>
              <TableCell>Incident Ref</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No statements found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              statements.map((statement) => (
                <TableRow key={statement.statement_id}>
                  <TableCell>{statement.statement_id}</TableCell>
                  <TableCell>{statement.incident_ref}</TableCell>
                  <TableCell>
                    <Chip
                      label={statement.status}
                      color={getStatusColor(statement.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(statement.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {statement.submitted_at
                      ? new Date(statement.submitted_at).toLocaleString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleEditStatement(statement)}>
                      {statement.status === 'draft' ? 'Edit' : 'View'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Statement Template</DialogTitle>
        <DialogContent>
          <StatementTemplateSelector onSelect={handleTemplateSelect} />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!selectedTemplate || !!editingStatement}
        onClose={() => {
          setSelectedTemplate(null);
          setEditingStatement(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingStatement?.statement_id ? 'Edit Statement' : 'Create Statement'}
        </DialogTitle>
        <DialogContent>
          <WitnessStatementForm
            template={selectedTemplate || editingStatement?.template}
            statement={editingStatement?.statement_id ? editingStatement : null}
            eventData={editingStatement?.eventData || null}
            onSave={handleStatementSaved}
            onCancel={() => {
              setSelectedTemplate(null);
              setEditingStatement(null);
            }}
          />
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default React.memo(WitnessStatementList);
